/** angular */
import { Component, Input, ViewChild, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { DsLibSearchBarComponent } from '../ds-lib-search-bar';
import { DsLibSearchAutocompleteOptionInterface } from './interfaces/ds-lib-search-autocomplete-option.interface';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { SelectPositionInterface } from './interfaces/select-position-interface';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
/** end angular */

@Component({
  selector: 'ds-lib-search-autocomplete',
  templateUrl: './ds-lib-search-autocomplete.component.html',
  styleUrls: ['./ds-lib-search-autocomplete.component.scss'],
})
export class DsLibSearchAutocompleteComponent implements AfterViewInit {
  /**
   *
   */
  public formGroup: FormGroup;

  /**
   * Observable firing when the search changes
   */
  public filteredOptions: Observable<DsLibSearchAutocompleteOptionInterface[]>;

  /**
   * The position of the mat-select in css values for top, left
   */
  public selectPosition: SelectPositionInterface = { top: '0', left: '0' };

  /**
   * Width of the dropdown in px
   */
  @Input() width = 200;

  /**
   * Height of the dropdown list in px.
   */
  @Input() height = 150;

  /**
   * Height of one mat-option (in px) for the virtual scroll.
   */
  @Input() itemSize = 30;

  /**
   * Placeholder for the search bar
   */
  @Input() placeholder = '';

  /**
   * Set the position of the mat-select.
   *
   * @param position SelectPositionInterface
   */
  @Input() set position(position: SelectPositionInterface) {
    this.selectPosition.top = position.top || this.selectPosition.top;
    this.selectPosition.left = position.left || this.selectPosition.left;
  }

  /**
   * Given control must have a value matching interface DsLibSearchAutocompleteOptionInterface.
   *
   * @param selectedControl FormControl
   */
  @Input() set selectedControl(selectedControl: FormControl) {
    this.formGroup = this.formBuilder.group({
      search: new FormControl(''),
      selected: selectedControl,
    });
    this.filteredOptions = this.formGroup.get('search').valueChanges.pipe(
      startWith(this.formGroup.get('search').value),
      map((search) => (search ? this.filterOptions(search) : this.options.slice()))
    );
    this.formGroup.get('selected').valueChanges.subscribe((selected) => {
      this.selectEmitter.emit(selected);
    });
  }

  /**
   * List of options without filter
   */
  @Input() options: DsLibSearchAutocompleteOptionInterface[] = [];

  /**
   * Outputs the selected option when it changes
   */
  @Output() selectEmitter = new EventEmitter<DsLibSearchAutocompleteOptionInterface>();

  /**
   * Emits everytime the panel is opened or closed, and gives its value
   */
  @Output() openedEmitter = new EventEmitter<boolean>();

  /**
   *
   */
  @ViewChild('searchBar') searchBar: DsLibSearchBarComponent;

  /**
   *
   */
  @ViewChild('matSelect') matSelect: MatSelect;

  /**
   *
   */
  @ViewChild(CdkVirtualScrollViewport)
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport;

  /**
   * Creates an instance of DsLibSearchAutocompleteComponent.
   *
   * @param formBuilder instances of a FormControl, FormGroup, or FormArray
   */
  constructor(private formBuilder: FormBuilder) {}

  /**
   * Opened Emitter.
   */
  ngAfterViewInit() {
    this.matSelect.openedChange.subscribe((opened) => {
      this.openedEmitter.emit(opened);
    });
  }

  /**
   * By design, the search string must be cleared every time we focus the search bar programmatically.
   */
  public focusSearch() {
    this.resetSearch();
    this.searchBar.focus();
  }

  /**
   * Resets the search bar value, and forces filteredOptions to refresh.
   */
  public resetSearch() {
    this.formGroup.get('search').setValue('');
  }

  /**
   * Return an array containing only options matching search string.
   *
   * @param search Search value
   * @returns DsLibSearchAutocompleteOptionInterface
   */
  public filterOptions(search: string) {
    const lowerSearch = search.toLowerCase();
    return this.options.filter((option) => option.name.toLowerCase().includes(lowerSearch));
  }

  /**
   * Compare with selected option.
   *
   * @param option the compared option
   * @param selectedOption the selected option
   * @returns boolean
   */
  public compareWithSelectedOption(
    option: DsLibSearchAutocompleteOptionInterface,
    selectedOption: DsLibSearchAutocompleteOptionInterface
  ) {
    return option && selectedOption ? option.id === selectedOption.id : option === selectedOption;
  }

  /**
   * Open mat-select.
   */
  public openMatselect() {
    this.matSelect.open();
    this.cdkVirtualScrollViewPort.scrollToIndex(0);
    this.cdkVirtualScrollViewPort.checkViewportSize();
  }
}
